/**
 * API slice for Copilot onboarding steps
 */

import { createApi, type EndpointBuilder } from '@reduxjs/toolkit/query/react';

import { getBaseUrlObNextGen } from '../../../constants/obNextGenConstants';
import { axiosBaseQuery } from '../../../services/baseQuery';

// API Response Types
export interface OnboardingStepsCategoryResponse {
  Configuration: {
    Name: string;
    order: number;
    subcategories?: {
      Name: string;
      order: number;
      parentOrder: number;
    }[];
  }[];
  parentCategoryOrder: number;
}

export interface GenerateCategoryTemplateRequest {
  applicationType: string;
  parentCategoryName: string;
  categoryNames: string[];
  techOnBoard?: boolean;
  endpointKey?: number;
}

export interface GenerateCategoryTemplateResponse {
  configuration: {
    appTypeKey: number;
    groups: {
      name: string;
      attributes: any[];
    }[];
    categoryName: string;
  };
  modifiedAttributes: unknown;
  draftType: number;
}

// File Upload Types
export interface GenerateInstructionsRequest {
  fileContent: string;
  fileName: string;
  contentType: string;
}

export interface GenerateInstructionsResponse {
  message: string;
  status: string;
}

// Dropdown API Types
interface FetchApiDataArgs {
  apiKey: string;
  endpoint: string;
  offset?: number;
  max?: number;
  findBy?: string;
}

// Validation API Types - API returns 'Valid' string or error message
type ValidateApplicationNameResponse = string;

// Onboarding API Types
interface InitiateOnboardPayload {
  saveCategoryRequest: {
    appTypeKey: number;
    draftType: number;
    endpointName: string;
    categories: {
      categoryName: string;
      categoryValue: {
        uncommitedValues: Record<string, any>;
        connectionType?: string;
      };
    }[];
  };
  deployData: {
    multipleconnection: boolean;
    [key: string]: any;
  };
  deploy: boolean;
}

interface InitiateOnboardResponse {
  endpointKey?: number;
  vaultAttributeMap?: Record<string, unknown>;
  saveDataToVaultResponseMap?: Record<string, unknown>;
  endpointSyncStatus?: string;
  timestamp?: string;
  exception?: string;
  message?: string;
  status?: number;
}

interface TestConnectionArgs {
  endpointName: string;
}

// Save Use Case Configuration Types
interface SaveUseCaseConfigurationArgs {
  applicationtype: string;
  endpointKey: number;
  body: unknown;
}

export const copilotApi = createApi({
  reducerPath: 'copilotApi',
  baseQuery: axiosBaseQuery({ baseUrl: getBaseUrlObNextGen() }),
  endpoints: (builder: EndpointBuilder<any, string, string>) => ({
    getOnboardingStepsCategory: builder.query<
      OnboardingStepsCategoryResponse,
      void
    >({
      query: () => ({
        url: '/idw/admin/applications/categories/apptype/CUA',
        method: 'GET',
      }),
    }),
    generateCategoryTemplate: builder.query<
      GenerateCategoryTemplateResponse,
      GenerateCategoryTemplateRequest
    >({
      query: ({
        parentCategoryName,
        categoryNames,
        applicationType,
        techOnBoard = true,
        endpointKey,
      }: GenerateCategoryTemplateRequest) => ({
        url: '/idw/admin/applications/v2/generatecategorytemplate',
        method: 'POST',
        data: {
          parentCategoryName,
          categoryNames,
          applicationType,
          techOnBoard,
          ...(endpointKey && { endpointKey }),
        },
      }),
    }),

    fetchApiData: builder.query<unknown, FetchApiDataArgs>({
      query: ({
        apiKey,
        endpoint,
        offset = 0,
        max = 25,
        findBy = '',
      }: FetchApiDataArgs) => ({
        url: `${apiKey}${endpoint}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { offset, max, findBy },
      }),
    }),

    validateApplicationName: builder.query<
      ValidateApplicationNameResponse,
      string
    >({
      query: (applicationName: string) => ({
        url: `/idw/admin/applications/validateapplicationname?applicationName=${applicationName}`,
        method: 'GET',
      }),
    }),

    initiateOnboard: builder.mutation<
      InitiateOnboardResponse,
      InitiateOnboardPayload
    >({
      query: (payload: InitiateOnboardPayload) => ({
        url: '/idw/admin/applications/initiateonboard',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: payload,
      }),
    }),

    testConnection: builder.mutation<unknown, TestConnectionArgs>({
      query: ({ endpointName }: TestConnectionArgs) => ({
        url: '/idw/admin/applications/testconnection',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: {
          endpointName,
          connectiontype: 'CUA',
          isOnBoarded: true,
          statusForEnableDisable: '1',
        },
      }),
    }),

    fetchAttributesData: builder.query<
      any,
      { apiURL: string; domainObjects: string[] }
    >({
      query: ({ apiURL, domainObjects }) => ({
        url: apiURL.replace('/api', ''),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: domainObjects,
      }),
    }),

    saveUseCaseConfiguration: builder.mutation<
      unknown,
      SaveUseCaseConfigurationArgs
    >({
      query: ({ applicationtype, endpointKey, body }) => ({
        url: `/idw/admin/applications/${applicationtype}/${endpointKey}`,
        method: 'PUT',
        data: body,
      }),
    }),

    updateApplication: builder.mutation<
      unknown,
      {
        applicationtype: string;
        endpointKey: number;
        FormData: Record<string, unknown>;
      }
    >({
      query: ({ applicationtype, endpointKey, FormData }) => ({
        url: `/idw/admin/applications/${applicationtype}/${endpointKey}`,
        method: 'PUT',
        data: FormData,
      }),
    }),

    generateInstructions: builder.mutation<
      GenerateInstructionsResponse,
      GenerateInstructionsRequest
    >({
      query: (payload) => ({
        url: '/onboarding/copilot/v1/generate-instructions',
        method: 'POST',
        data: payload,
        config: {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      }),
    }),
  }),
});

export const {
  useGetOnboardingStepsCategoryQuery,
  useLazyGenerateCategoryTemplateQuery,
  useLazyFetchApiDataQuery,
  useLazyValidateApplicationNameQuery,
  useInitiateOnboardMutation,
  useTestConnectionMutation,
  useFetchAttributesDataQuery,
  useSaveUseCaseConfigurationMutation,
  useUpdateApplicationMutation,
  useGenerateInstructionsMutation,
} = copilotApi;
